// skills/smithing/smithing.js
// Smithing skill logic

import { BARS, ARROW_HEADS, SWORDS, DIRKS, GREATSWORDS, MELEE_ARMOR, BULWARKS, TOOLS } from './smithingData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { awardDrops, canFitItem } from '../../systems/drops.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { getTalentLootBonus, getTalentFlatBonus, rollTalentProc, incrementStackCount, resetStackCount } from '../../systems/talentTree/talentTree.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

class SmithingSkill extends BaseSkill {
    constructor() {
        super(SKILLS.SMITHING, TOOLS, 'PYRESTONE');
        // Furnace Mastery ash counter (tracks bars smelted since last ash award)
        this.furnaceMasteryCounter = 0;
        // Forge Momentum - next action completes instantly
        this.nextActionInstant = false;
    }

    /**
     * Get the currently equipped tool object (alias for BaseSkill.getCurrentTool)
     */
    get currentTool() {
        return this.getCurrentTool();
    }

    /**
     * Roll for talent proc effects and apply them
     * Returns object with proc results
     */
    rollSmithingProcs(itemType = 'bar') {
        const procs = {
            coalSaved: false,
            resourceSaved: false,
            forgeInstant: false,
            bulkCommission: false
        };

        // Coal save - 8% per rank (for items requiring coal/deepcoal)
        if (rollTalentProc(SKILLS.SMITHING, 'coalSave')) {
            procs.coalSaved = true;
        }

        // Resource save - 5% per rank
        if (rollTalentProc(SKILLS.SMITHING, 'resourceSave')) {
            procs.resourceSaved = true;
        }

        // Forge Momentum - 5% instant completion
        if (rollTalentProc(SKILLS.SMITHING, 'forgeInstant')) {
            procs.forgeInstant = true;
        }

        // Bulk Commission - 10% extra equipment (equipment only)
        if (itemType === 'equipment' && rollTalentProc(SKILLS.SMITHING, 'bulkCommission')) {
            procs.bulkCommission = true;
        }

        return procs;
    }


    canSmith(barId) {
        // Find bar by id property, not by object key
        const bar = Object.values(BARS).find(b => b.id === barId);
        if (!bar) return { success: false, reason: 'Bar not found' };

        const skill = gameState.getSkill(SKILLS.SMITHING);
        if (skill.level < bar.level) {
            return { success: false, reason: `Requires level ${bar.level} Smithing` };
        }

        // Check for required ore in inventory
        const hasOre = gameState.getItemCount(bar.inputItem) >= bar.inputQuantity;
        if (!hasOre) {
            const itemName = bar.inputItem.replace(/_/g, ' ');
            return { success: false, reason: `Need ${bar.inputQuantity} ${itemName}` };
        }

        // Check for secondary item (e.g., deepcoal for blackiron and voidsteel)
        if (bar.secondaryItem) {
            const hasSecondary = gameState.getItemCount(bar.secondaryItem) >= bar.secondaryQuantity;
            if (!hasSecondary) {
                const itemName = bar.secondaryItem.replace(/_/g, ' ');
                return { success: false, reason: `Need ${bar.secondaryQuantity} ${itemName}` };
            }
        }

        // Check if output can fit in inventory
        const outputItem = bar.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startSmithing(barId, showToast = true) {
        // Find bar by id property, not by object key
        const bar = Object.values(BARS).find(b => b.id === barId);
        const canSmith = this.canSmith(barId);

        if (!canSmith.success) {
            if (showToast) toastWarning(canSmith.reason);
            return false;
        }

        // Calculate smithing time including talent bonuses
        const baseTime = bar.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const smithingTime = baseTime / speedMultiplier;

        const action = {
            type: 'smithing',
            name: `Smithing ${bar.name}`,
            bar: bar,
            barId: barId.toUpperCase(),
            progress: 0,
            duration: smithingTime,
            startTime: Date.now(),
            forceComplete: false,

            // Tick function called every game tick
            // speedMultiplier param allows admin panel to speed up actions
            tick: (speedMultiplier = 1) => {
                // Check if we still have materials - stop immediately if not
                const hasMaterials = gameState.getItemCount(bar.inputItem) >= bar.inputQuantity;
                const hasSecondary = !bar.secondaryItem || gameState.getItemCount(bar.secondaryItem) >= bar.secondaryQuantity;
                if (!hasMaterials || !hasSecondary) {
                    gameState.clearAction();
                    return;
                }

                // Forge Momentum - instant completion from previous proc
                if (this.nextActionInstant) {
                    this.nextActionInstant = false;
                    action.progress = 1;
                }

                // Check for force complete (used by admin panel)
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeSmithing(bar);
                    const canContinue = this.canSmith(barId);
                    if (canContinue.success) {
                        this.startSmithing(barId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Apply speed multiplier to elapsed time
                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeSmithing(bar);
                    const canContinue = this.canSmith(barId);
                    if (canContinue.success) {
                        this.startSmithing(barId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);

        return true;
    }

    completeSmithing(bar) {
        // Roll for talent procs first
        const procs = this.rollSmithingProcs('bar');

        // Get output item for proc notifications
        const outputItemId = bar.drops[0]?.itemId;

        // Consume ore from inventory (unless resource saved)
        if (!procs.resourceSaved) {
            const removed = gameState.removeItem(bar.inputItem, bar.inputQuantity);
            if (!removed) {
                logger.warn('Failed to remove ore for smithing');
                this.stopSmithing();
                return;
            }
        } else {
            logger.game('Resource Saver proc! Saved materials.');
            recordTalentProc(SKILLS.SMITHING, 'Resource Saver', 'Materials saved!', null);
        }

        // Consume secondary item if required (e.g., deepcoal) - unless coal saved
        if (bar.secondaryItem) {
            if (!procs.coalSaved) {
                const removedSecondary = gameState.removeItem(bar.secondaryItem, bar.secondaryQuantity);
                if (!removedSecondary) {
                    logger.warn('Failed to remove secondary item for smithing');
                    this.stopSmithing();
                    return;
                }
            } else {
                logger.game('Efficient Fuel proc! Saved coal.');
                recordTalentProc(SKILLS.SMITHING, 'Efficient Fuel', 'Coal saved!', null);
            }
        }

        // Pass bar.id for admin node XP override
        const xpResult = addXP(SKILLS.SMITHING, bar.xp, bar.id);

        // Calculate output quantity with talent bonuses
        let outputMultiplier = 1;

        // Apply Tempered Output loot bonus
        const lootBonus = getTalentLootBonus(SKILLS.SMITHING);
        if (lootBonus > 0) {
            outputMultiplier *= (1 + lootBonus);
        }

        // Prepare drops with modified quantity (probabilistic rounding for single-quantity items)
        const modifiedDrops = bar.drops.map(drop => {
            const rawQty = drop.quantity * outputMultiplier;
            const baseQty = Math.floor(rawQty);
            const fractional = rawQty - baseQty;
            const quantity = baseQty + (Math.random() < fractional ? 1 : 0);
            return { ...drop, quantity };
        });

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.SMITHING, modifiedDrops);

        // Furnace Mastery - generate ash from smelting
        const furnaceMastery = getTalentFlatBonus(SKILLS.SMITHING, 'furnaceMastery');
        if (furnaceMastery > 0) {
            this.furnaceMasteryCounter++;
            // High tier bars (level >= 50): 1 ash per 3 crafts
            // Low tier bars (level < 50): 1 ash per 5 crafts
            const threshold = bar.level >= 50 ? 3 : 5;
            if (this.furnaceMasteryCounter >= threshold) {
                gameState.addItem('ashes', 1);
                this.furnaceMasteryCounter = 0;
                logger.game('Furnace Mastery! Collected ashes from the forge.');
                recordTalentProc(SKILLS.SMITHING, 'Furnace Mastery', null, null, { itemId: 'ashes', quantity: 1 });
            }
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.SMITHING);

        // Check for pilferer contract assistance (rolls once per action completion)
        pilferingSkill.tryContractAssist('smithing');

        // Increment stacking speed count (Heat Retention talent)
        incrementStackCount(SKILLS.SMITHING, `bar_${bar.id}`);

        // Forge Momentum - next action instant
        if (procs.forgeInstant) {
            this.nextActionInstant = true;
            recordTalentProc(SKILLS.SMITHING, 'Forge Momentum', 'Instant next action!', null);
        }

        this.onSmithingComplete(bar, xpResult);
    }

    stopSmithing() {
        resetStackCount(SKILLS.SMITHING);
        gameState.clearAction();
    }

    // ============================================
    // Arrow Head Smithing Methods
    // ============================================

    canSmithArrowheads(arrowheadId) {
        const arrowhead = Object.values(ARROW_HEADS).find(a => a.id === arrowheadId);
        if (!arrowhead) return { success: false, reason: 'Arrowhead type not found' };

        const skill = gameState.getSkill(SKILLS.SMITHING);
        if (skill.level < arrowhead.level) {
            return { success: false, reason: `Requires level ${arrowhead.level} Smithing` };
        }

        // Check for required bar in inventory
        const hasBar = gameState.getItemCount(arrowhead.inputItem) >= arrowhead.inputQuantity;
        if (!hasBar) {
            const itemName = arrowhead.inputItem.replace(/_/g, ' ');
            return { success: false, reason: `Need ${arrowhead.inputQuantity} ${itemName}` };
        }

        // Check if output can fit in inventory
        const outputItem = arrowhead.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startSmithingArrowheads(arrowheadId, showToast = true) {
        const arrowhead = Object.values(ARROW_HEADS).find(a => a.id === arrowheadId);
        const canSmith = this.canSmithArrowheads(arrowheadId);

        if (!canSmith.success) {
            if (showToast) toastWarning(canSmith.reason);
            return false;
        }

        // Calculate smithing time including talent bonuses
        const baseTime = arrowhead.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const smithingTime = baseTime / speedMultiplier;

        const action = {
            type: 'smithing',
            name: `Smithing ${arrowhead.name}`,
            arrowhead: arrowhead,
            arrowheadId: arrowheadId,
            progress: 0,
            duration: smithingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                // Check if we still have materials - stop immediately if not
                const hasMaterials = gameState.getItemCount(arrowhead.inputItem) >= arrowhead.inputQuantity;
                if (!hasMaterials) {
                    gameState.clearAction();
                    return;
                }

                // Forge Momentum - instant completion from previous proc
                if (this.nextActionInstant) {
                    this.nextActionInstant = false;
                    action.progress = 1;
                }

                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeSmithingArrowheads(arrowhead);
                    const canContinue = this.canSmithArrowheads(arrowheadId);
                    if (canContinue.success) {
                        this.startSmithingArrowheads(arrowheadId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeSmithingArrowheads(arrowhead);
                    const canContinue = this.canSmithArrowheads(arrowheadId);
                    if (canContinue.success) {
                        this.startSmithingArrowheads(arrowheadId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);

        return true;
    }

    completeSmithingArrowheads(arrowhead) {
        // Roll for talent procs
        const procs = this.rollSmithingProcs('arrowhead');
        const outputItemId = arrowhead.drops[0]?.itemId;

        // Consume bar from inventory (unless resource saved)
        if (!procs.resourceSaved) {
            const removed = gameState.removeItem(arrowhead.inputItem, arrowhead.inputQuantity);
            if (!removed) {
                logger.warn('Failed to remove bar for arrowheads');
                this.stopSmithingArrowheads();
                return;
            }
        } else {
            logger.game('Resource Saver proc! Saved materials.');
            recordTalentProc(SKILLS.SMITHING, 'Resource Saver', 'Materials saved!', null);
        }

        const xpResult = addXP(SKILLS.SMITHING, arrowhead.xp, arrowhead.id);

        // Calculate output quantity with talent bonuses
        let outputMultiplier = 1;
        const lootBonus = getTalentLootBonus(SKILLS.SMITHING);
        if (lootBonus > 0) {
            outputMultiplier *= (1 + lootBonus);
        }

        // Apply Arrowsmith bonus (+20% arrowhead output)
        const arrowheadOutput = getTalentFlatBonus(SKILLS.SMITHING, 'arrowheadOutput');
        if (arrowheadOutput > 0) {
            outputMultiplier *= (1 + arrowheadOutput);
        }

        // Apply Fletcher's Supply bonus (+50% arrowhead output)
        const fletcherSupply = getTalentFlatBonus(SKILLS.SMITHING, 'fletcherSupply');
        if (fletcherSupply > 0) {
            outputMultiplier *= (1 + fletcherSupply);
            logger.game("Fletcher's Supply active! +50% arrowheads!");
        }

        // Prepare drops with modified quantity (probabilistic rounding for fractional bonus)
        const modifiedDrops = arrowhead.drops.map(drop => {
            const rawQty = drop.quantity * outputMultiplier;
            const baseQty = Math.floor(rawQty);
            const fractional = rawQty - baseQty;
            const quantity = baseQty + (Math.random() < fractional ? 1 : 0);
            return { ...drop, quantity };
        });

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.SMITHING, modifiedDrops);

        // Roll for corruption proc
        rollForCorruption(SKILLS.SMITHING);

        // Increment stacking speed count (Heat Retention talent)
        incrementStackCount(SKILLS.SMITHING, `arrowhead_${arrowhead.id}`);

        // Forge Momentum - next action instant
        if (procs.forgeInstant) {
            this.nextActionInstant = true;
            recordTalentProc(SKILLS.SMITHING, 'Forge Momentum', 'Instant next action!', null);
        }

        this.onSmithingArrowheadsComplete(arrowhead, xpResult);
    }

    stopSmithingArrowheads() {
        resetStackCount(SKILLS.SMITHING);
        gameState.clearAction();
    }

    getAvailableArrowheads() {
        const skill = gameState.getSkill(SKILLS.SMITHING);
        return Object.values(ARROW_HEADS).filter(ah => ah.level <= skill.level);
    }

    onSmithingArrowheadsComplete(arrowhead, xpResult) {
        logger.game(`Smithed ${arrowhead.name}! +${arrowhead.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Smithing level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ============================================
    // Sword Smithing Methods
    // ============================================

    canSmithSword(swordId) {
        const sword = Object.values(SWORDS).find(s => s.id === swordId);
        if (!sword) return { success: false, reason: 'Sword type not found' };

        const skill = gameState.getSkill(SKILLS.SMITHING);
        if (skill.level < sword.level) {
            return { success: false, reason: `Requires level ${sword.level} Smithing` };
        }

        // Check for required bar in inventory
        const hasBar = gameState.getItemCount(sword.inputItem) >= sword.inputQuantity;
        if (!hasBar) {
            const itemName = sword.inputItem.replace(/_/g, ' ');
            return { success: false, reason: `Need ${sword.inputQuantity} ${itemName}` };
        }

        // Check if output can fit in inventory
        const outputItem = sword.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startSmithingSword(swordId, showToast = true) {
        const sword = Object.values(SWORDS).find(s => s.id === swordId);
        const canSmith = this.canSmithSword(swordId);

        if (!canSmith.success) {
            if (showToast) toastWarning(canSmith.reason);
            return false;
        }

        // Calculate smithing time including talent bonuses
        const baseTime = sword.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const smithingTime = baseTime / speedMultiplier;

        const action = {
            type: 'smithing',
            name: `Forging ${sword.name}`,
            sword: sword,
            swordId: swordId,
            progress: 0,
            duration: smithingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                // Check if we still have materials - stop immediately if not
                const hasMaterials = gameState.getItemCount(sword.inputItem) >= sword.inputQuantity;
                if (!hasMaterials) {
                    gameState.clearAction();
                    return;
                }

                // Forge Momentum - instant completion from previous proc
                if (this.nextActionInstant) {
                    this.nextActionInstant = false;
                    action.progress = 1;
                }

                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeSmithingSword(sword);
                    const canContinue = this.canSmithSword(swordId);
                    if (canContinue.success) {
                        this.startSmithingSword(swordId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeSmithingSword(sword);
                    const canContinue = this.canSmithSword(swordId);
                    if (canContinue.success) {
                        this.startSmithingSword(swordId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);

        return true;
    }

    completeSmithingSword(sword) {
        // Roll for talent procs
        const procs = this.rollSmithingProcs('equipment');
        const outputItemId = sword.drops[0]?.itemId;

        // Consume bars from inventory (unless resource saved)
        if (!procs.resourceSaved) {
            const removed = gameState.removeItem(sword.inputItem, sword.inputQuantity);
            if (!removed) {
                logger.warn('Failed to remove bars for sword');
                this.stopSmithingSword();
                return;
            }
        } else {
            logger.game('Resource Saver proc! Saved materials.');
            recordTalentProc(SKILLS.SMITHING, 'Resource Saver', 'Materials saved!', null);
        }

        // Calculate XP with Equipment Expertise bonus
        let effectiveXP = sword.xp;
        const equipmentXPBonus = getTalentFlatBonus(SKILLS.SMITHING, 'equipmentXPBonus');
        if (equipmentXPBonus > 0) {
            effectiveXP = Math.floor(sword.xp * (1 + equipmentXPBonus));
        }
        const xpResult = addXP(SKILLS.SMITHING, effectiveXP, sword.id);

        // Calculate output quantity with talent bonuses
        let outputMultiplier = 1;
        const lootBonus = getTalentLootBonus(SKILLS.SMITHING);
        if (lootBonus > 0) {
            outputMultiplier *= (1 + lootBonus);
        }
        // Prepare drops with modified quantity (probabilistic rounding for fractional bonus)
        const modifiedDrops = sword.drops.map(drop => {
            const rawQty = drop.quantity * outputMultiplier;
            const baseQty = Math.floor(rawQty);
            const fractional = rawQty - baseQty;
            const quantity = baseQty + (Math.random() < fractional ? 1 : 0);
            return { ...drop, quantity };
        });

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.SMITHING, modifiedDrops);

        // Bulk Commission - extra equipment drop
        if (procs.bulkCommission && sword.drops[0]) {
            gameState.addItem(sword.drops[0].itemId, 1);
            recordTalentProc(SKILLS.SMITHING, 'Bulk Commission', null, null, { itemId: sword.drops[0].itemId, quantity: 1 });
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.SMITHING);

        // Increment stacking speed count (Heat Retention talent)
        incrementStackCount(SKILLS.SMITHING, `sword_${sword.id}`);

        // Forge Momentum - next action instant
        if (procs.forgeInstant) {
            this.nextActionInstant = true;
            recordTalentProc(SKILLS.SMITHING, 'Forge Momentum', 'Instant next action!', null);
        }

        this.onSmithingSwordComplete(sword, xpResult);
    }

    stopSmithingSword() {
        resetStackCount(SKILLS.SMITHING);
        gameState.clearAction();
    }

    getAvailableSwords() {
        const skill = gameState.getSkill(SKILLS.SMITHING);
        return Object.values(SWORDS).filter(s => s.level <= skill.level);
    }

    onSmithingSwordComplete(sword, xpResult) {
        logger.game(`Forged ${sword.name}! +${sword.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Smithing level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ============================================
    // Dirk Smithing Methods
    // ============================================

    canSmithDirk(dirkId) {
        const dirk = Object.values(DIRKS).find(d => d.id === dirkId);
        if (!dirk) return { success: false, reason: 'Dirk type not found' };

        const skill = gameState.getSkill(SKILLS.SMITHING);
        if (skill.level < dirk.level) {
            return { success: false, reason: `Requires level ${dirk.level} Smithing` };
        }

        const hasBar = gameState.getItemCount(dirk.inputItem) >= dirk.inputQuantity;
        if (!hasBar) {
            const itemName = dirk.inputItem.replace(/_/g, ' ');
            return { success: false, reason: `Need ${dirk.inputQuantity} ${itemName}` };
        }

        // Check if output can fit in inventory
        const outputItem = dirk.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startSmithingDirk(dirkId, showToast = true) {
        const dirk = Object.values(DIRKS).find(d => d.id === dirkId);
        const canSmith = this.canSmithDirk(dirkId);

        if (!canSmith.success) {
            if (showToast) toastWarning(canSmith.reason);
            return false;
        }

        // Calculate smithing time including talent bonuses
        const baseTime = dirk.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const smithingTime = baseTime / speedMultiplier;

        const action = {
            type: 'smithing',
            name: `Forging ${dirk.name}`,
            dirk: dirk,
            dirkId: dirkId,
            progress: 0,
            duration: smithingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                const hasMaterials = gameState.getItemCount(dirk.inputItem) >= dirk.inputQuantity;
                if (!hasMaterials) {
                    gameState.clearAction();
                    return;
                }

                // Forge Momentum - instant completion from previous proc
                if (this.nextActionInstant) {
                    this.nextActionInstant = false;
                    action.progress = 1;
                }

                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeSmithingDirk(dirk);
                    const canContinue = this.canSmithDirk(dirkId);
                    if (canContinue.success) {
                        this.startSmithingDirk(dirkId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeSmithingDirk(dirk);
                    const canContinue = this.canSmithDirk(dirkId);
                    if (canContinue.success) {
                        this.startSmithingDirk(dirkId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);

        return true;
    }

    completeSmithingDirk(dirk) {
        // Roll for talent procs
        const procs = this.rollSmithingProcs('equipment');
        const outputItemId = dirk.drops[0]?.itemId;

        // Consume bar from inventory (unless resource saved)
        if (!procs.resourceSaved) {
            const removed = gameState.removeItem(dirk.inputItem, dirk.inputQuantity);
            if (!removed) {
                logger.warn('Failed to remove bar for dirk');
                this.stopSmithingDirk();
                return;
            }
        } else {
            logger.game('Resource Saver proc! Saved materials.');
            recordTalentProc(SKILLS.SMITHING, 'Resource Saver', 'Materials saved!', null);
        }

        // Calculate XP with Equipment Expertise bonus
        let effectiveXP = dirk.xp;
        const equipmentXPBonus = getTalentFlatBonus(SKILLS.SMITHING, 'equipmentXPBonus');
        if (equipmentXPBonus > 0) {
            effectiveXP = Math.floor(dirk.xp * (1 + equipmentXPBonus));
        }
        const xpResult = addXP(SKILLS.SMITHING, effectiveXP, dirk.id);

        // Calculate output quantity with talent bonuses
        let outputMultiplier = 1;
        const lootBonus = getTalentLootBonus(SKILLS.SMITHING);
        if (lootBonus > 0) {
            outputMultiplier *= (1 + lootBonus);
        }
        const modifiedDrops = dirk.drops.map(drop => {
            const rawQty = drop.quantity * outputMultiplier;
            const baseQty = Math.floor(rawQty);
            const fractional = rawQty - baseQty;
            const quantity = baseQty + (Math.random() < fractional ? 1 : 0);
            return { ...drop, quantity };
        });
        awardDrops(SKILLS.SMITHING, modifiedDrops);

        // Bulk Commission - extra equipment drop
        if (procs.bulkCommission && dirk.drops[0]) {
            gameState.addItem(dirk.drops[0].itemId, 1);
            recordTalentProc(SKILLS.SMITHING, 'Bulk Commission', null, null, { itemId: dirk.drops[0].itemId, quantity: 1 });
        }

        rollForCorruption(SKILLS.SMITHING);

        // Increment stacking speed count (Heat Retention talent)
        incrementStackCount(SKILLS.SMITHING, `dirk_${dirk.id}`);

        // Forge Momentum - next action instant
        if (procs.forgeInstant) {
            this.nextActionInstant = true;
            recordTalentProc(SKILLS.SMITHING, 'Forge Momentum', 'Instant next action!', null);
        }

        this.onSmithingDirkComplete(dirk, xpResult);
    }

    stopSmithingDirk() {
        resetStackCount(SKILLS.SMITHING);
        gameState.clearAction();
    }

    onSmithingDirkComplete(dirk, xpResult) {
        logger.game(`Forged ${dirk.name}! +${dirk.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Smithing level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ============================================
    // Greatsword Smithing Methods
    // ============================================

    canSmithGreatsword(greatswordId) {
        const greatsword = Object.values(GREATSWORDS).find(g => g.id === greatswordId);
        if (!greatsword) return { success: false, reason: 'Greatsword type not found' };

        const skill = gameState.getSkill(SKILLS.SMITHING);
        if (skill.level < greatsword.level) {
            return { success: false, reason: `Requires level ${greatsword.level} Smithing` };
        }

        const hasBar = gameState.getItemCount(greatsword.inputItem) >= greatsword.inputQuantity;
        if (!hasBar) {
            const itemName = greatsword.inputItem.replace(/_/g, ' ');
            return { success: false, reason: `Need ${greatsword.inputQuantity} ${itemName}` };
        }

        // Check if output can fit in inventory
        const outputItem = greatsword.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startSmithingGreatsword(greatswordId, showToast = true) {
        const greatsword = Object.values(GREATSWORDS).find(g => g.id === greatswordId);
        const canSmith = this.canSmithGreatsword(greatswordId);

        if (!canSmith.success) {
            if (showToast) toastWarning(canSmith.reason);
            return false;
        }

        // Calculate smithing time including talent bonuses
        const baseTime = greatsword.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const smithingTime = baseTime / speedMultiplier;

        const action = {
            type: 'smithing',
            name: `Forging ${greatsword.name}`,
            greatsword: greatsword,
            greatswordId: greatswordId,
            progress: 0,
            duration: smithingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                const hasMaterials = gameState.getItemCount(greatsword.inputItem) >= greatsword.inputQuantity;
                if (!hasMaterials) {
                    gameState.clearAction();
                    return;
                }

                // Forge Momentum - instant completion from previous proc
                if (this.nextActionInstant) {
                    this.nextActionInstant = false;
                    action.progress = 1;
                }

                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeSmithingGreatsword(greatsword);
                    const canContinue = this.canSmithGreatsword(greatswordId);
                    if (canContinue.success) {
                        this.startSmithingGreatsword(greatswordId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeSmithingGreatsword(greatsword);
                    const canContinue = this.canSmithGreatsword(greatswordId);
                    if (canContinue.success) {
                        this.startSmithingGreatsword(greatswordId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);

        return true;
    }

    completeSmithingGreatsword(greatsword) {
        // Roll for talent procs
        const procs = this.rollSmithingProcs('equipment');
        const outputItemId = greatsword.drops[0]?.itemId;

        // Consume bars from inventory (unless resource saved)
        if (!procs.resourceSaved) {
            const removed = gameState.removeItem(greatsword.inputItem, greatsword.inputQuantity);
            if (!removed) {
                logger.warn('Failed to remove bars for greatsword');
                this.stopSmithingGreatsword();
                return;
            }
        } else {
            logger.game('Resource Saver proc! Saved materials.');
            recordTalentProc(SKILLS.SMITHING, 'Resource Saver', 'Materials saved!', null);
        }

        // Calculate XP with Equipment Expertise bonus
        let effectiveXP = greatsword.xp;
        const equipmentXPBonus = getTalentFlatBonus(SKILLS.SMITHING, 'equipmentXPBonus');
        if (equipmentXPBonus > 0) {
            effectiveXP = Math.floor(greatsword.xp * (1 + equipmentXPBonus));
        }
        const xpResult = addXP(SKILLS.SMITHING, effectiveXP, greatsword.id);

        // Calculate output quantity with talent bonuses
        let outputMultiplier = 1;
        const lootBonus = getTalentLootBonus(SKILLS.SMITHING);
        if (lootBonus > 0) {
            outputMultiplier *= (1 + lootBonus);
        }
        const modifiedDrops = greatsword.drops.map(drop => {
            const rawQty = drop.quantity * outputMultiplier;
            const baseQty = Math.floor(rawQty);
            const fractional = rawQty - baseQty;
            const quantity = baseQty + (Math.random() < fractional ? 1 : 0);
            return { ...drop, quantity };
        });
        awardDrops(SKILLS.SMITHING, modifiedDrops);

        // Bulk Commission - extra equipment drop
        if (procs.bulkCommission && greatsword.drops[0]) {
            gameState.addItem(greatsword.drops[0].itemId, 1);
            recordTalentProc(SKILLS.SMITHING, 'Bulk Commission', null, null, { itemId: greatsword.drops[0].itemId, quantity: 1 });
        }

        rollForCorruption(SKILLS.SMITHING);

        // Increment stacking speed count (Heat Retention talent)
        incrementStackCount(SKILLS.SMITHING, `greatsword_${greatsword.id}`);

        // Forge Momentum - next action instant
        if (procs.forgeInstant) {
            this.nextActionInstant = true;
            recordTalentProc(SKILLS.SMITHING, 'Forge Momentum', 'Instant next action!', null);
        }

        this.onSmithingGreatswordComplete(greatsword, xpResult);
    }

    stopSmithingGreatsword() {
        resetStackCount(SKILLS.SMITHING);
        gameState.clearAction();
    }

    onSmithingGreatswordComplete(greatsword, xpResult) {
        logger.game(`Forged ${greatsword.name}! +${greatsword.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Smithing level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ============================================
    // Melee Armor Smithing Methods
    // ============================================

    canSmithMeleeArmor(armorId) {
        // Find armor recipe in MELEE_ARMOR by id
        let armor = null;
        for (const tierRecipes of Object.values(MELEE_ARMOR)) {
            for (const recipe of Object.values(tierRecipes)) {
                if (recipe.id === armorId) {
                    armor = recipe;
                    break;
                }
            }
            if (armor) break;
        }

        if (!armor) return { success: false, reason: 'Armor type not found' };

        const skill = gameState.getSkill(SKILLS.SMITHING);
        if (skill.level < armor.level) {
            return { success: false, reason: `Requires level ${armor.level} Smithing` };
        }

        const hasBar = gameState.getItemCount(armor.inputItem) >= armor.inputQuantity;
        if (!hasBar) {
            const itemName = armor.inputItem.replace(/_/g, ' ');
            return { success: false, reason: `Need ${armor.inputQuantity} ${itemName}` };
        }

        // Check if output can fit in inventory
        const outputItem = armor.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startSmithingMeleeArmor(armorId, showToast = true) {
        // Find armor recipe
        let armor = null;
        for (const tierRecipes of Object.values(MELEE_ARMOR)) {
            for (const recipe of Object.values(tierRecipes)) {
                if (recipe.id === armorId) {
                    armor = recipe;
                    break;
                }
            }
            if (armor) break;
        }

        const canSmith = this.canSmithMeleeArmor(armorId);

        if (!canSmith.success) {
            if (showToast) toastWarning(canSmith.reason);
            return false;
        }

        // Calculate smithing time including talent bonuses
        const baseTime = armor.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const smithingTime = baseTime / speedMultiplier;

        const action = {
            type: 'smithing',
            name: `Smithing ${armor.name}`,
            meleeArmor: armor,
            meleeArmorId: armorId,
            progress: 0,
            duration: smithingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                const hasMaterials = gameState.getItemCount(armor.inputItem) >= armor.inputQuantity;
                if (!hasMaterials) {
                    gameState.clearAction();
                    return;
                }

                // Forge Momentum - instant completion from previous proc
                if (this.nextActionInstant) {
                    this.nextActionInstant = false;
                    action.progress = 1;
                }

                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeSmithingMeleeArmor(armor);
                    const canContinue = this.canSmithMeleeArmor(armorId);
                    if (canContinue.success) {
                        this.startSmithingMeleeArmor(armorId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeSmithingMeleeArmor(armor);
                    const canContinue = this.canSmithMeleeArmor(armorId);
                    if (canContinue.success) {
                        this.startSmithingMeleeArmor(armorId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);

        return true;
    }

    completeSmithingMeleeArmor(armor) {
        // Roll for talent procs
        const procs = this.rollSmithingProcs('equipment');
        const outputItemId = armor.drops[0]?.itemId;

        // Consume bars from inventory (unless resource saved)
        if (!procs.resourceSaved) {
            const removed = gameState.removeItem(armor.inputItem, armor.inputQuantity);
            if (!removed) {
                logger.warn('Failed to remove bars for armor');
                this.stopSmithingMeleeArmor();
                return;
            }
        } else {
            logger.game('Resource Saver proc! Saved materials.');
            recordTalentProc(SKILLS.SMITHING, 'Resource Saver', 'Materials saved!', null);
        }

        // Calculate XP with Equipment Expertise bonus
        let effectiveXP = armor.xp;
        const equipmentXPBonus = getTalentFlatBonus(SKILLS.SMITHING, 'equipmentXPBonus');
        if (equipmentXPBonus > 0) {
            effectiveXP = Math.floor(armor.xp * (1 + equipmentXPBonus));
        }
        const xpResult = addXP(SKILLS.SMITHING, effectiveXP, armor.id);

        // Calculate output quantity with talent bonuses
        let outputMultiplier = 1;
        const lootBonus = getTalentLootBonus(SKILLS.SMITHING);
        if (lootBonus > 0) {
            outputMultiplier *= (1 + lootBonus);
        }
        const modifiedDrops = armor.drops.map(drop => {
            const rawQty = drop.quantity * outputMultiplier;
            const baseQty = Math.floor(rawQty);
            const fractional = rawQty - baseQty;
            const quantity = baseQty + (Math.random() < fractional ? 1 : 0);
            return { ...drop, quantity };
        });
        awardDrops(SKILLS.SMITHING, modifiedDrops);

        // Bulk Commission - extra equipment drop
        if (procs.bulkCommission && armor.drops[0]) {
            gameState.addItem(armor.drops[0].itemId, 1);
            recordTalentProc(SKILLS.SMITHING, 'Bulk Commission', null, null, { itemId: armor.drops[0].itemId, quantity: 1 });
        }

        rollForCorruption(SKILLS.SMITHING);

        // Increment stacking speed count (Heat Retention talent)
        incrementStackCount(SKILLS.SMITHING, `armor_${armor.id}`);

        // Forge Momentum - next action instant
        if (procs.forgeInstant) {
            this.nextActionInstant = true;
            recordTalentProc(SKILLS.SMITHING, 'Forge Momentum', 'Instant next action!', null);
        }

        this.onSmithingMeleeArmorComplete(armor, xpResult);
    }

    stopSmithingMeleeArmor() {
        resetStackCount(SKILLS.SMITHING);
        gameState.clearAction();
    }

    onSmithingMeleeArmorComplete(armor, xpResult) {
        logger.game(`Smithed ${armor.name}! +${armor.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Smithing level up! Now level ${xpResult.newLevel}`);
        }
    }

    // ============================================
    // Bulwark Smithing Methods
    // ============================================

    canSmithBulwark(bulwarkId) {
        const bulwark = Object.values(BULWARKS).find(b => b.id === bulwarkId);

        if (!bulwark) return { success: false, reason: 'Bulwark type not found' };

        const skill = gameState.getSkill(SKILLS.SMITHING);
        if (skill.level < bulwark.level) {
            return { success: false, reason: `Requires level ${bulwark.level} Smithing` };
        }

        const hasBar = gameState.getItemCount(bulwark.inputItem) >= bulwark.inputQuantity;
        if (!hasBar) {
            const itemName = bulwark.inputItem.replace(/_/g, ' ');
            return { success: false, reason: `Need ${bulwark.inputQuantity} ${itemName}` };
        }

        // Check if output can fit in inventory
        const outputItem = bulwark.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startSmithingBulwark(bulwarkId, showToast = true) {
        const bulwark = Object.values(BULWARKS).find(b => b.id === bulwarkId);

        const canSmith = this.canSmithBulwark(bulwarkId);

        if (!canSmith.success) {
            if (showToast) toastWarning(canSmith.reason);
            return false;
        }

        // Calculate smithing time including talent bonuses
        const baseTime = bulwark.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const smithingTime = baseTime / speedMultiplier;

        const action = {
            type: 'smithing',
            name: `Smithing ${bulwark.name}`,
            bulwark: bulwark,
            bulwarkId: bulwarkId,
            progress: 0,
            duration: smithingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                const hasMaterials = gameState.getItemCount(bulwark.inputItem) >= bulwark.inputQuantity;
                if (!hasMaterials) {
                    gameState.clearAction();
                    return;
                }

                // Forge Momentum - instant completion from previous proc
                if (this.nextActionInstant) {
                    this.nextActionInstant = false;
                    action.progress = 1;
                }

                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeSmithingBulwark(bulwark);
                    const canContinue = this.canSmithBulwark(bulwarkId);
                    if (canContinue.success) {
                        this.startSmithingBulwark(bulwarkId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeSmithingBulwark(bulwark);
                    const canContinue = this.canSmithBulwark(bulwarkId);
                    if (canContinue.success) {
                        this.startSmithingBulwark(bulwarkId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);

        return true;
    }

    completeSmithingBulwark(bulwark) {
        // Roll for talent procs
        const procs = this.rollSmithingProcs('equipment');
        const outputItemId = bulwark.drops[0]?.itemId;

        // Consume bars from inventory (unless resource saved)
        if (!procs.resourceSaved) {
            const removed = gameState.removeItem(bulwark.inputItem, bulwark.inputQuantity);
            if (!removed) {
                logger.warn('Failed to remove bars for bulwark');
                this.stopSmithingBulwark();
                return;
            }
        } else {
            logger.game('Resource Saver proc! Saved materials.');
            recordTalentProc(SKILLS.SMITHING, 'Resource Saver', 'Materials saved!', null);
        }

        // Calculate XP with Equipment Expertise bonus
        let effectiveXP = bulwark.xp;
        const equipmentXPBonus = getTalentFlatBonus(SKILLS.SMITHING, 'equipmentXPBonus');
        if (equipmentXPBonus > 0) {
            effectiveXP = Math.floor(bulwark.xp * (1 + equipmentXPBonus));
        }
        const xpResult = addXP(SKILLS.SMITHING, effectiveXP, bulwark.id);

        // Calculate output quantity with talent bonuses
        let outputMultiplier = 1;
        const lootBonus = getTalentLootBonus(SKILLS.SMITHING);
        if (lootBonus > 0) {
            outputMultiplier *= (1 + lootBonus);
        }
        const modifiedDrops = bulwark.drops.map(drop => {
            const rawQty = drop.quantity * outputMultiplier;
            const baseQty = Math.floor(rawQty);
            const fractional = rawQty - baseQty;
            const quantity = baseQty + (Math.random() < fractional ? 1 : 0);
            return { ...drop, quantity };
        });
        awardDrops(SKILLS.SMITHING, modifiedDrops);

        // Bulk Commission - extra equipment drop
        if (procs.bulkCommission && bulwark.drops[0]) {
            gameState.addItem(bulwark.drops[0].itemId, 1);
            recordTalentProc(SKILLS.SMITHING, 'Bulk Commission', null, null, { itemId: bulwark.drops[0].itemId, quantity: 1 });
        }

        rollForCorruption(SKILLS.SMITHING);

        // Increment stacking speed count (Heat Retention talent)
        incrementStackCount(SKILLS.SMITHING, `bulwark_${bulwark.id}`);

        // Forge Momentum - next action instant
        if (procs.forgeInstant) {
            this.nextActionInstant = true;
            recordTalentProc(SKILLS.SMITHING, 'Forge Momentum', 'Instant next action!', null);
        }

        this.onSmithingBulwarkComplete(bulwark, xpResult);
    }

    stopSmithingBulwark() {
        resetStackCount(SKILLS.SMITHING);
        gameState.clearAction();
    }

    onSmithingBulwarkComplete(bulwark, xpResult) {
        logger.game(`Smithed ${bulwark.name}! +${bulwark.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Smithing level up! Now level ${xpResult.newLevel}`);
        }
    }

    /**
     * Purchase or equip a tool
     */
    upgradeTool(toolId) {
        const tool = Object.values(TOOLS).find(t => t.id === toolId);
        if (!tool) return { success: false, reason: 'Invalid tool' };

        const skill = gameState.getSkill(SKILLS.SMITHING);
        if (skill.level < tool.level) {
            return { success: false, reason: `Requires level ${tool.level} Smithing` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.SMITHING, toolId)) {
            // Just equip it
            gameState.equipTool(SKILLS.SMITHING, toolId);
            return { success: true, action: 'equipped' };
        }

        // Try to purchase
        const purchaseResult = gameState.purchaseTool(SKILLS.SMITHING, toolId, tool.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        // Auto-equip after purchase
        gameState.equipTool(SKILLS.SMITHING, toolId);
        return { success: true, action: 'purchased' };
    }

    getAvailableBars() {
        const skill = gameState.getSkill(SKILLS.SMITHING);
        return Object.values(BARS).filter(bar => bar.level <= skill.level);
    }

    onSmithingComplete(bar, xpResult) {
        logger.game(`Smithed ${bar.name}! +${bar.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Smithing level up! Now level ${xpResult.newLevel}`);
        }
    }
}

export const smithingSkill = new SmithingSkill();
export default smithingSkill;